import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import {FiAward, FiStar, FiTrendingUp, FiCalendar, FiTarget,
  FiShield, FiHeart, FiWind, FiDroplet, FiZap,
  FiShare2, FiDownload, FiX,  FiLock, FiUser} from "react-icons/fi";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";
import { useCheckIn } from "../../context/CheckInContext";
import { userAPI, practiceVideoAPI, dietPlanAPI, liveSessionAPI, achievementAPI } from "../../services/api";
import { toast } from "react-toastify";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  orange: "#F29A0C", orange2: "#E97818",
  olive: "#6B8A5A",  olive2: "#8BA47A",
  dark: "#1F1F1F",   mid: "#555",
  muted: "#999",     light: "#CCC",
  border: "rgba(239,229,216,0.8)",
};

const glass = {
  background: "rgba(255,255,255,0.82)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

// ─── TIER CONFIG ─────────────────────────────────────────────────────────────
const TIER_CONFIG = {
  bronze:   { label: "Bronze",   grad: "linear-gradient(135deg,#CD7F32,#A0522D)", ring: "rgba(205,127,50,0.2)",  text: "rgba(160,82,45,0.9)",  glow: "rgba(205,127,50,0.4)" },
  silver:   { label: "Silver",   grad: "linear-gradient(135deg,#9CA3AF,#6B7280)", ring: "rgba(156,163,175,0.2)", text: "rgba(107,114,128,0.9)", glow: "rgba(156,163,175,0.4)" },
  gold:     { label: "Gold",     grad: `linear-gradient(135deg,${T.orange},${T.orange2})`, ring: "rgba(242,154,12,0.2)", text: "rgba(200,102,10,0.9)", glow: "rgba(242,154,12,0.45)" },
  platinum: { label: "Platinum", grad: `linear-gradient(135deg,${T.olive},${T.olive2})`,   ring: "rgba(107,138,90,0.2)", text: "rgba(62,92,46,0.9)",   glow: "rgba(107,138,90,0.4)" },
};

const CATS = ["All", "Practice", "Mindfulness", "Wellness", "Milestone"];
const MEDALS = { 1: "🥇", 2: "🥈", 3: "🥉" };

// ─── TILT CARD ────────────────────────────────────────────────────────────────
function TiltCard({ children, style = {}, className = "", intensity = 7 }) {
  const ref = useRef(null);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [intensity, -intensity]), { stiffness: 260, damping: 28 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-intensity, intensity]), { stiffness: 260, damping: 28 });
  return (
    <motion.div ref={ref}
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", perspective: 900, ...style }}
      className={className}
      onMouseMove={e => { const r = ref.current.getBoundingClientRect(); mx.set((e.clientX - r.left) / r.width - 0.5); my.set((e.clientY - r.top) / r.height - 0.5); }}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
    >{children}</motion.div>
  );
}

// ─── MINI PROGRESS RING ───────────────────────────────────────────────────────
function MiniRing({ pct, size = 64, stroke = 5, color = T.orange }) {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} stroke="rgba(239,229,216,0.5)" strokeWidth={stroke} fill="none" />
      <motion.circle cx={size/2} cy={size/2} r={r} stroke={color} strokeWidth={stroke} fill="none"
        strokeLinecap="round" strokeDasharray={circ}
        initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: offset }}
        transition={{ duration: 1.2, ease: "easeOut" }} />
    </svg>
  );
}

// ─── BADGE CARD ───────────────────────────────────────────────────────────────
// ─── BADGE CARD ───────────────────────────────────────────────────────────────
function BadgeCard({ badge, delay, onClick }) {
  const tc = TIER_CONFIG[badge.tier] || TIER_CONFIG.bronze;
  const Icon = badge.icon;

  const formattedEarnedDate = useMemo(() => {
    if (!badge.unlocked || !badge.date) return null;
    return badge.date;
  }, [badge.unlocked, badge.date]);

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      whileHover={badge.unlocked ? { y: -8, scale: 1.02, boxShadow: `0 24px 60px rgba(242,154,12,0.18)` } : { y: -4, scale: 1.01 }}
      onClick={() => badge.unlocked && onClick(badge)}
      className="relative rounded-[24px] p-5 text-center overflow-hidden flex flex-col justify-between"
      style={{
        background: badge.unlocked 
          ? "rgba(255,255,255,0.92)" 
          : "linear-gradient(145deg, rgba(255,253,250,0.95), rgba(246,242,236,0.95))",
        border: badge.unlocked 
          ? `1px solid rgba(242,154,12,0.25)` 
          : `1px dashed rgba(242,154,12,0.35)`,
        boxShadow: badge.unlocked 
          ? "0 16px 50px rgba(0,0,0,0.06), inset 0 1px 0 #fff" 
          : "0 10px 30px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.8)",
        cursor: badge.unlocked ? "pointer" : "default",
        transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
      }}>

      {/* Premium High-Visibility Status Pill (Top-Right) */}
      {!badge.unlocked && (
        <div className="absolute top-2.5 right-2.5 px-2.5 py-1 rounded-full flex items-center gap-1 text-[8.5px] font-black uppercase tracking-wider z-20"
          style={{
            background: badge.progress > 0 ? "rgba(242,154,12,0.18)" : "rgba(160,160,160,0.18)",
            color: badge.progress > 0 ? "#C8660A" : "#555",
            border: `1px solid ${badge.progress > 0 ? "rgba(242,154,12,0.4)" : "rgba(180,180,180,0.4)"}`,
            backdropFilter: "blur(6px)"
          }}>
          <FiLock size={9} />
          <span>{badge.progress > 0 ? "In Progress" : "Locked"}</span>
        </div>
      )}

      {/* Shimmer for unlocked */}
      {badge.unlocked && (
        <motion.div className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent)", x: "-120%" }}
          whileHover={{ x: "120%" }} transition={{ duration: 0.6 }} />
      )}

      <div>
        {/* Badge Icon Container */}
        <motion.div
          initial={badge.unlocked ? { scale: 0.5, rotate: -15, opacity: 0 } : {}}
          whileInView={badge.unlocked ? { scale: 1, rotate: 0, opacity: 1 } : {}}
          viewport={{ once: true }}
          transition={{ delay: delay + 0.1, type: "spring", stiffness: 260, damping: 18 }}
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3.5 relative mt-2"
          style={{
            background: badge.unlocked 
              ? tc.grad 
              : "linear-gradient(135deg, rgba(230,225,218,0.8), rgba(210,205,198,0.8))",
            boxShadow: badge.unlocked 
              ? `0 12px 30px ${tc.glow}` 
              : "0 6px 18px rgba(0,0,0,0.04)",
            border: badge.unlocked ? "none" : "2px solid rgba(255,255,255,0.8)"
          }}>
          <Icon size={26} style={{ color: badge.unlocked ? "#fff" : "#888" }} />
          {badge.unlocked && (
            <>
              <motion.div animate={{ opacity: [0, 1, 0], scale: [0, 1, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full"
                style={{ background: tc.glow }} />
              <motion.div animate={{ opacity: [0, 1, 0], scale: [0, 1, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.9 }}
                className="absolute -bottom-1 -left-1 w-2.5 h-2.5 rounded-full"
                style={{ background: tc.glow }} />
            </>
          )}
        </motion.div>

        {/* Badge Title & Category */}
        <p className="font-black text-[13px] mb-1 leading-tight" style={{ fontFamily: "'Poppins',sans-serif",
          letterSpacing: "-0.2px", color: badge.unlocked ? T.dark : "#444" }}>
          {badge.title}
        </p>
        <p className="text-[10px] capitalize mb-3 font-semibold" style={{ color: badge.unlocked ? T.muted : "#888" }}>
          {badge.cat}
        </p>

        {/* Tier & Points Tag */}
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full"
            style={{ background: tc.ring, color: tc.text }}>{tc.label}</span>
          <span className="text-[10px] font-black" style={{ color: badge.unlocked ? T.orange : "#888" }}>
            +{badge.pts} pts
          </span>
        </div>
      </div>

      {/* Bottom Section: Earned Date ONLY for unlocked, or Sleek Progress Bar for locked */}
      <div className="mt-2 pt-2 border-t border-zinc-150/60">
        {badge.unlocked && formattedEarnedDate ? (
          <p className="text-[10px] font-bold text-[#6B8A5A] m-0">
            Earned on {formattedEarnedDate}
          </p>
        ) : (
          <div className="w-full">
            <div className="flex justify-between items-center text-[10px] font-bold mb-1">
              <span className="text-[#888]">Progress</span>
              <span className="text-[#E97818]">{badge.progress || 0}%</span>
            </div>
            <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: "rgba(242,154,12,0.12)" }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: "linear-gradient(90deg, #F29A0C, #E97818)", boxShadow: "0 2px 6px rgba(242,154,12,0.4)" }}
                initial={{ width: 0 }}
                animate={{ width: `${badge.progress || 0}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── BADGE DETAIL MODAL ───────────────────────────────────────────────────────
function BadgeModal({ badge, onClose }) {
  const tc = TIER_CONFIG[badge.tier];
  const Icon = badge.icon;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(14px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.85, y: 24 }} animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.85 }} transition={{ type: "spring", stiffness: 320, damping: 28 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md rounded-[28px] overflow-hidden"
        style={{ boxShadow: "0 40px 100px rgba(0,0,0,0.45)" }}>
        <div className="relative p-8 text-center" style={{ background: tc.grad }}>
          <div className="absolute inset-0 opacity-[0.08]"
            style={{ backgroundImage: "radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize: "16px 16px" }} />
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer", color: "#fff" }}>
            <FiX size={15} />
          </button>
          <motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 3, repeat: Infinity }}
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(10px)" }}>
            <Icon size={36} style={{ color: "#fff" }} />
          </motion.div>
          <h2 className="font-black text-white text-[22px] mb-1.5"
            style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>{badge.title}</h2>
          <p className="text-white/75 text-[13px]">{tc.label} Badge · +{badge.pts} points</p>
        </div>
        <div style={{ background: "#fff", padding: "24px 28px" }}>
          <p className="text-[14px] leading-[1.7] mb-5" style={{ color: T.mid }}>{badge.desc}</p>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="rounded-[14px] p-3.5" style={{ background: "rgba(255,244,232,0.7)" }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.7px] mb-1" style={{ color: "#C8660A" }}>Category</p>
              <p className="font-black text-[14px] text-[#1F1F1F] capitalize">{badge.cat}</p>
            </div>
            <div className="rounded-[14px] p-3.5" style={{ background: "rgba(245,250,242,0.7)" }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.7px] mb-1" style={{ color: "#27500A" }}>Earned On</p>
              <p className="font-black text-[14px] text-[#1F1F1F]">{badge.date}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} onClick={onClose}
              className="flex-1 h-12 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2"
              style={{ background: tc.grad, boxShadow: `0 10px 28px ${tc.glow}`, border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              <FiShare2 size={15} /> Share Achievement
            </motion.button>
            <button onClick={onClose} className="h-12 px-5 rounded-full font-semibold text-[13px]"
              style={{ background: "rgba(200,200,200,0.12)", border: `1px solid ${T.border}`, color: T.muted, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── CERTIFICATE MODAL ────────────────────────────────────────────────────────
function CertModal({ cert, onClose }) {
  const handleDownload = () => {
    if (!cert.unlocked) {
      toast.info("This certificate is currently locked. Complete the program requirement to unlock!");
      return;
    }

    const userName = (() => {
      try {
        const u = localStorage.getItem("divyayoga_user");
        return u ? JSON.parse(u).fullName : "Yogi Practitioner";
      } catch {
        return "Yogi Practitioner";
      }
    })();

    // Only real credential IDs from the backend — never invent one
    const certCode = cert.code || "Pending issue";
    const hasRealCode = Boolean(cert.code);

    let pTitle = "CERTIFICATE OF EXCELLENCE";
    let pCredType = "International Board Credential";
    let pCurriculum = "for successfully fulfilling all international academic standards, practical competencies, and curriculum requirements in holistic yoga science.";
    let pPrimaryColor = "#D4AF37";
    let pSecondaryColor = "#B8860B";
    let pBgGradient = "linear-gradient(135deg, #FFFCF7 0%, #F9F5EC 100%)";
    let pSealTop = "DIVYYOGA";
    let pSealBot = "CERTIFIED";

    if (cert.title.includes("Pranayama")) {
      pTitle = "INTERNATIONAL MASTER CERTIFICATE IN PRANAYAMA";
      pCredType = "Advanced Breathwork & Energy Mastery";
      pCurriculum = "for demonstrating exemplary mastery in sacred pranayama techniques, vital energy channel regulation, kumbhaka breath retention, and rhythmic energy control.";
      pPrimaryColor = "#27500A";
      pSecondaryColor = "#6B8A5A";
      pBgGradient = "linear-gradient(135deg, #F5FAF2 0%, #EBF4E7 100%)";
      pSealTop = "PRANAYAMA";
      pSealBot = "MASTER";
    } else if (cert.title.includes("Mindfulness")) {
      pTitle = "INTERNATIONAL SPECIALIST CREDENTIAL IN MINDFULNESS";
      pCredType = "Mindfulness Meditation & Inner Balance";
      pCurriculum = "for achieving advanced competencies in mindfulness meditation, vipassana awareness, mental clarity, and emotional equilibrium.";
      pPrimaryColor = "#534AB7";
      pSecondaryColor = "#7F77DD";
      pBgGradient = "linear-gradient(135deg, #F7F6FE 0%, #EEEDFE 100%)";
      pSealTop = "MINDFULNESS";
      pSealBot = "SPECIALIST";
    } else {
      pTitle = "INTERNATIONAL DIPLOMA IN YOGA FOUNDATIONS";
      pCredType = "Foundational Yoga & Asana Mastery";
      pCurriculum = "for successfully mastering foundational yoga asanas, body alignment principles, sun salutations, and daily practice discipline.";
      pPrimaryColor = "#D4AF37";
      pSecondaryColor = "#B8860B";
      pBgGradient = "linear-gradient(135deg, #FFFCF7 0%, #F9F5EC 100%)";
      pSealTop = "FOUNDATIONS";
      pSealBot = "DIPLOMA";
    }

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${pTitle} - ${userName}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700;800;900&family=Cinzel+Decorative:wght@700;900&family=Great+Vibes&family=Montserrat:wght@400;500;600;700;800;900&family=Cormorant+Garamond:ital,wght@0,600;0,700;1,500;1,700&display=swap');
            @page { size: A4 landscape; margin: 0; }
            * { box-sizing: border-box; }
            body {
              margin: 0; padding: 0; background-color: #0B0F17;
              font-family: 'Montserrat', sans-serif;
              display: flex; justify-content: center; align-items: center; min-height: 100vh;
              -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
            }
            .cert-outer-frame {
              width: 1100px; height: 760px; padding: 22px;
              background: linear-gradient(135deg, #0B0F17 0%, #1A2332 50%, #0B0F17 100%);
              border-radius: 12px; box-shadow: 0 35px 90px rgba(0,0,0,0.6), inset 0 0 40px rgba(0,0,0,0.8); position: relative;
            }
            .cert-inner-frame {
              width: 100%; height: 100%; padding: 40px 54px;
              background: ${pBgGradient};
              border: 4px double ${pPrimaryColor}; outline: 2px solid ${pSecondaryColor}; outline-offset: -12px;
              position: relative; text-align: center; display: flex; flex-direction: column; justify-content: space-between;
              box-shadow: inset 0 0 60px rgba(0,0,0,0.04); overflow: hidden;
            }
            /* Filigree Ornaments */
            .corner-filigree { position: absolute; width: 55px; height: 55px; border: 3.5px double ${pPrimaryColor}; pointer-events: none; z-index: 10; }
            .tl { top: 18px; left: 18px; border-right: none; border-bottom: none; }
            .tr { top: 18px; right: 18px; border-left: none; border-bottom: none; }
            .bl { bottom: 18px; left: 18px; border-right: none; border-top: none; }
            .br { bottom: 18px; right: 18px; border-left: none; border-top: none; }

            /* Centered Watermark Logo Seal */
            .watermark-logo {
              position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
              width: 360px; height: 360px; opacity: 0.06; pointer-events: none; z-index: 1;
              border-radius: 50%; object-fit: cover; filter: sepia(30%) contrast(110%);
            }

            /* Watermark Security Pattern */
            .security-pattern {
              position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
              width: 540px; height: 540px; opacity: 0.035; pointer-events: none; z-index: 1;
            }

            .header-badge { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 8px; margin-bottom: 2px; position: relative; z-index: 5; }
            .logo-emblem { width: 68px; height: 68px; border-radius: 50%; object-fit: cover; border: 3px solid ${pPrimaryColor}; box-shadow: 0 6px 18px rgba(0,0,0,0.18); margin: 0 auto; }
            .org-name { font-family: 'Montserrat', 'Inter', sans-serif; font-size: 26px; font-weight: 900; letter-spacing: 3.5px; color: #111827; margin: 0; text-align: center; text-transform: uppercase; }
            .org-subtitle { font-size: 10.5px; font-weight: 800; letter-spacing: 4.5px; color: ${pSecondaryColor}; text-transform: uppercase; margin-top: 3px; text-align: center; }

            .divider-gold { width: 290px; height: 2px; background: linear-gradient(90deg, transparent, ${pPrimaryColor}, transparent); margin: 8px auto 12px; position: relative; z-index: 5; }
            .cert-type { font-family: 'Cinzel', serif; font-size: 13.5px; font-weight: 800; letter-spacing: 6px; color: ${pSecondaryColor}; text-transform: uppercase; margin-bottom: 4px; position: relative; z-index: 5; }
            .cert-main-title { font-family: 'Cinzel', serif; font-size: 27px; font-weight: 800; color: #111827; letter-spacing: 1.5px; margin: 0 0 12px; text-transform: uppercase; position: relative; z-index: 5; }

            .user-fullname {
              font-family: 'Cinzel', serif; font-size: 42px; font-weight: 900;
              background: linear-gradient(135deg, #111827 0%, ${pPrimaryColor} 60%, ${pSecondaryColor} 100%);
              -webkit-background-clip: text; -webkit-text-fill-color: transparent;
              padding-bottom: 6px; border-bottom: 2.5px solid ${pPrimaryColor}65; display: inline-block; min-width: 500px; margin: 0 auto 12px;
              position: relative; z-index: 5;
            }
            .cert-body-text { font-family: 'Cormorant Garamond', serif; font-size: 17.5px; font-weight: 600; line-height: 1.65; color: #374151; max-width: 760px; margin: 0 auto 14px; position: relative; z-index: 5; }

            .footer-grid { display: flex; align-items: flex-end; justify-content: space-between; padding-top: 14px; border-top: 1.5px solid ${pPrimaryColor}35; position: relative; z-index: 5; }
            
            .verification-box { text-align: left; font-size: 9.5px; color: #6B7280; line-height: 1.55; display: flex; items-center; gap: 10px; }
            .qr-code-img { width: 48px; height: 48px; border-radius: 6px; border: 1px solid ${pPrimaryColor}55; padding: 2px; background: #fff; }
            .verification-info { display: flex; flex-direction: column; justify-content: center; }
            .verification-info strong { color: #111827; font-family: 'Cinzel', serif; }

            /* 3D Wax Seal Badge */
            .seal-container { position: relative; margin: 0 auto; width: 100px; height: 100px; }
            .ribbon-left { position: absolute; bottom: -18px; left: 16px; width: 20px; height: 42px; background: ${pPrimaryColor}; transform: rotate(18deg); clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 85%, 0 100%); box-shadow: 0 4px 10px rgba(0,0,0,0.2); }
            .ribbon-right { position: absolute; bottom: -18px; right: 16px; width: 20px; height: 42px; background: ${pPrimaryColor}; transform: rotate(-18deg); clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 85%, 0 100%); box-shadow: 0 4px 10px rgba(0,0,0,0.2); }
            .gold-seal-badge {
              position: relative; z-index: 10; width: 100%; height: 100%;
              background: radial-gradient(circle, #FFF 0%, ${pPrimaryColor} 60%, ${pSecondaryColor} 100%);
              border-radius: 50%; border: 3.5px solid #FFF; box-shadow: 0 10px 28px rgba(0,0,0,0.3);
              display: flex; flex-direction: column; align-items: center; justify-content: center; color: #FFF;
            }
            .seal-inner-ring { width: 82px; height: 82px; border: 1.5px dashed #FFF; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
            .seal-text-top { font-size: 7px; font-weight: 900; letter-spacing: 1.2px; color: #FFF; }
            .seal-star { font-size: 13px; color: #FFF; margin: 1px 0; }
            .seal-text-bot { font-size: 7px; font-weight: 900; letter-spacing: 1px; color: #FFF; }

            .signature-container { display: flex; gap: 24px; align-items: flex-end; }
            .signature-block { text-align: center; min-width: 150px; }
            .script-sig { font-family: 'Great Vibes', cursive; font-size: 28px; color: ${pPrimaryColor}; margin-bottom: -6px; }
            .sig-line { border-top: 1.5px solid #9CA3AF; padding-top: 4px; font-size: 10.5px; font-weight: 800; color: #111827; }
            .sig-sub { font-size: 8.5px; color: #6B7280; font-weight: 600; }

            .accreditation-bar { font-size: 8px; font-weight: 800; tracking: 2px; letter-spacing: 2px; color: ${pSecondaryColor}; margin-top: 10px; text-transform: uppercase; border-top: 1px dashed ${pPrimaryColor}40; padding-top: 6px; }

            @media print {
              body { background: none; }
              .cert-outer-frame { box-shadow: none; width: 100vw; height: 100vh; padding: 12px; }
            }
          </style>
        </head>
        <body>
          <div class="cert-outer-frame">
            <div class="cert-inner-frame">
              <div class="corner-filigree tl"></div><div class="corner-filigree tr"></div>
              <div class="corner-filigree bl"></div><div class="corner-filigree br"></div>

              <!-- Centered Watermark Logo Seal -->
              <img src="/assets/logo.jpeg" class="watermark-logo" alt="Divyyoga Board Watermark Logo" />

              <!-- Guilloche Security Pattern -->
              <svg class="security-pattern" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50" cy="50" r="45" fill="none" stroke="${pPrimaryColor}" stroke-width="0.5" stroke-dasharray="1 1"/>
                <circle cx="50" cy="50" r="35" fill="none" stroke="${pPrimaryColor}" stroke-width="0.75"/>
                <path d="M50 5 L55 95 M5 50 L95 50 M15 15 L85 85 M15 85 L85 15" stroke="${pPrimaryColor}" stroke-width="0.25"/>
              </svg>

              <div>
                <div class="header-badge">
                  <img src="/assets/logo.jpeg" class="logo-emblem" alt="Divyyoga Foundation Logo" />
                  <div style="text-align: center;">
                    <h1 class="org-name">DIVYYOGA FOUNDATION</h1>
                    <div class="org-subtitle">International Board of Yoga, Mindfulness & Holistic Wellness</div>
                  </div>
                </div>
                <div class="divider-gold"></div>
              </div>

              <div>
                <div class="cert-type">${pCredType}</div>
                <h2 class="cert-main-title">${pTitle}</h2>
                <div style="font-size:12.5px; font-style:italic; color:#6B7280; margin-bottom:6px; font-family:'Montserrat',sans-serif;">This official international board credential is proudly awarded to</div>
                <div class="user-fullname">${userName}</div>
                <div class="cert-body-text">${pCurriculum}</div>
              </div>

              <div>
                <div class="footer-grid">
                  <div class="verification-box">
                    ${hasRealCode ? `<img src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https://divyayoga.com/verify/${certCode}" class="qr-code-img" alt="Verification QR Code" />` : ""}
                    <div class="verification-info">
                      <div><strong>Credential ID:</strong> ${certCode}</div>
                      <div><strong>Issued Date:</strong> ${cert.date || "—"}</div>
                      <div><strong>Board Issuer:</strong> Divyyog International Board</div>
                      <div style="margin-top:2px; font-size:8px; color:#4B5563;">Scan QR to verify authenticity</div>
                    </div>
                  </div>

                  <div class="seal-container">
                    <div class="ribbon-left"></div>
                    <div class="ribbon-right"></div>
                    <div class="gold-seal-badge">
                      <div class="seal-inner-ring">
                        <div class="seal-text-top">${pSealTop}</div>
                        <div class="seal-star">★ OFFICIAL ★</div>
                        <div class="seal-text-bot">${pSealBot}</div>
                      </div>
                    </div>
                  </div>

                  <div class="signature-block">
                    <div class="script-sig">Divya Nithin Bhojwani</div>
                    <div class="sig-line">Divya Nithin Bhojwani</div>
                    <div class="sig-sub">Founder & International Acharya</div>
                  </div>
                </div>

                <div class="accreditation-bar">
                  ★ ACCREDITED BY INTERNATIONAL BOARD OF YOGA SCIENCE · ISO 9001:2025 CERTIFIED · REGISTRATION NO: ${certCode} ★
                </div>
              </div>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 600); };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(16px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.88, y: 20 }} animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.88 }} transition={{ type: "spring", stiffness: 320, damping: 28 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-xl rounded-[32px] overflow-hidden"
        style={{ boxShadow: "0 45px 120px rgba(0,0,0,0.6)" }}>
        <div className="relative p-7 text-center"
          style={{ background: cert.unlocked ? `linear-gradient(145deg,${cert.accent},${cert.accent2})` : "linear-gradient(145deg, #1F2937, #111827)" }}>
          <div className="absolute inset-0 opacity-[0.08]"
            style={{ backgroundImage: "radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize: "14px 14px" }} />
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer", color: "#fff" }}>
            <FiX size={15} />
          </button>
          
          {cert.unlocked ? (
            <FiAward size={44} style={{ color: "#fff", marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
          ) : (
            <FiLock size={44} style={{ color: "#F29A0C", marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
          )}

          <h2 className="font-black text-white text-[19px] mb-1"
            style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>{cert.title}</h2>
          <p className="text-white/80 text-[12px] font-semibold">{cert.prog}</p>
        </div>

        <div style={{ background: "#fff", padding: "24px 30px" }}>
          {/* Ultra Luxury Certificate Preview Box */}
          <div className="rounded-[24px] p-6 text-center mb-5 relative overflow-hidden shadow-2xl"
            style={{ 
              border: cert.unlocked ? `3px double ${cert.accent}` : `2px dashed rgba(180,180,180,0.5)`, 
              background: cert.unlocked ? "linear-gradient(135deg, #FFFCF7 0%, #F9F5EC 100%)" : "rgba(245,245,245,0.9)",
              boxShadow: cert.unlocked ? `0 16px 40px ${cert.accent}25` : "none" 
            }}>
            
            {/* Centered Watermark Logo behind text in Preview */}
            {cert.unlocked && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 opacity-[0.06] pointer-events-none z-0">
                <img src="/assets/logo.jpeg" alt="Divyyoga Logo Watermark" className="w-full h-full object-cover rounded-full filter sepia" />
              </div>
            )}

            {/* Watermark SVG inside preview */}
            {cert.unlocked && (
              <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 opacity-[0.035] pointer-events-none" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="none" stroke={cert.accent} strokeWidth="1"/>
                <path d="M50 5 L55 95 M5 50 L95 50" stroke={cert.accent} strokeWidth="0.5"/>
              </svg>
            )}

            <div className="flex flex-col items-center justify-center text-center gap-1.5 mb-2 relative z-10">
              <img src="/assets/logo.jpeg" alt="Divyyoga Foundation" className="w-10 h-10 rounded-full object-cover border-2 shadow-md" style={{ borderColor: cert.accent }} />
              <span className="text-[13px] font-black tracking-widest text-[#111827] text-center block uppercase" style={{ fontFamily: "'Montserrat', 'Inter', sans-serif" }}>DIVYYOGA FOUNDATION</span>
            </div>
            
            <p className="text-[9px] font-black uppercase tracking-[2.5px] mb-1 relative z-10" style={{ color: cert.accent }}>
              International Board of Yoga & Wellness
            </p>
            <p className="font-serif text-[17px] font-extrabold text-[#111827] mb-2 leading-tight uppercase relative z-10" style={{ letterSpacing: "0.5px" }}>
              {cert.title}
            </p>
            
            {cert.unlocked ? (
              <div className="relative z-10">
                <p className="text-[11px] font-medium text-[#6B7280] italic mb-1.5">This official credential is proudly awarded to</p>
                <p className="font-serif font-black text-[23px] text-[#D4AF37] mb-2" style={{ letterSpacing: "-0.5px" }}>
                  {(() => {
                    try {
                      const u = localStorage.getItem("divyayoga_user");
                      return u ? JSON.parse(u).fullName : "Yogi Practitioner";
                    } catch {
                      return "Yogi Practitioner";
                    }
                  })()}
                </p>
                <p className="text-[11.5px] text-[#4B5563] mb-3 leading-relaxed">{cert.prog}</p>
                <div className="flex items-center justify-between pt-3 border-t border-[#D4AF37]/30 text-[9.5px] text-[#6B7280]">
                  <div className="flex items-center gap-2">
                    {cert.code && (
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=https://divyayoga.com/verify/${cert.code}`} className="w-7 h-7 rounded border border-amber-300" alt="QR" />
                    )}
                    <div className="text-left">
                      <div>ID: <strong>{cert.code || "Pending issue"}</strong></div>
                      <div>Issued: <strong>{cert.date || "—"}</strong></div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-[#6B8A5A] block">Board Acharya</span>
                    <span className="text-[8.5px] text-zinc-500">Divya Nithin Bhojwani</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-3 relative z-10">
                <div className="px-3.5 py-1.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 inline-flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-wider mb-3">
                  <FiLock size={12} /> Certificate Locked
                </div>
                <p className="text-[12px] font-bold text-[#374151] mb-2">Requirement to Unlock:</p>
                <p className="text-[12.5px] font-bold text-[#D97706] mb-3 leading-snug px-4">{cert.requirement || "Complete program milestones"}</p>
                <div className="w-full bg-zinc-200 h-2.5 rounded-full overflow-hidden mb-1">
                  <div className="bg-gradient-to-r from-amber-500 to-amber-600 h-full rounded-full transition-all duration-700" style={{ width: `${cert.progress || 0}%` }} />
                </div>
                <p className="text-[10.5px] font-bold text-zinc-500">Progress: {cert.progress || 0}%</p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            {cert.unlocked ? (
              <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} onClick={handleDownload}
                className="flex-1 h-12 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(135deg,${cert.accent},${cert.accent2})`,
                  boxShadow: `0 10px 28px ${cert.accent}45`, border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
                <FiDownload size={15} /> Download PDF Certificate
              </motion.button>
            ) : (
              <button disabled className="flex-1 h-12 rounded-full bg-zinc-200 text-zinc-500 font-bold text-[13px] flex items-center justify-center gap-2 cursor-not-allowed">
                <FiLock size={14} /> Locked ({cert.progress || 0}%)
              </button>
            )}
            <button onClick={onClose} className="h-12 px-5 rounded-full font-semibold text-[13px]"
              style={{ background: "rgba(200,200,200,0.12)", border: `1px solid ${T.border}`,
                color: T.muted, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function AchievementsPage() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [activeTab, setActiveTab]       = useState("all");
  const [badgeModal, setBadgeModal]     = useState(null);
  const [certModal, setCertModal]       = useState(null);

  // Dynamic user data states from API
  const [achievementsData, setAchievementsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        setLoading(true);
        const res = await achievementAPI.getSummary();
        if (res.data?.success && res.data.data) {
          setAchievementsData(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch achievements:", err);
        toast.error("Failed to load achievements data from server.");
      } finally {
        setLoading(false);
      }
    };
    fetchAchievements();
  }, []);

  const BADGES = useMemo(() => {
    if (!achievementsData?.badges) return [];
    const iconMap = {
      "Beginner Yogi": FiAward,
      "7-Day Warrior": FiZap,
      "Mindful Explorer": FiHeart,
      "Pranayama Pioneer": FiWind,
      "30-Day Champion": FiCalendar,
      "Consistency Master": FiTarget,
      "Asana Adept": FiUser,
      "Wellness Guardian": FiShield,
      "Breathwork Sage": FiWind,
      "Century Practitioner": FiAward,
      "Nutrition Ninja": FiDroplet,
      "Level 3 Yogi": FiStar,
      "60-Day Streak": FiZap,
      "YTT Graduate": FiAward,
      "Global Yogi": FiTrendingUp,
      "Iron Practice": FiTarget
    };
    return achievementsData.badges.map(b => ({
      ...b,
      icon: iconMap[b.title] || FiAward
    }));
  }, [achievementsData]);

  const MILESTONES = useMemo(() => {
    if (!achievementsData?.milestones) return [];
    const iconMap = {
      "Beginner Yogi Badge Unlocked": FiAward,
      "7-Day Warrior Badge Unlocked": FiZap,
      "Mindful Explorer Badge Unlocked": FiHeart,
      "Pranayama Pioneer Badge Unlocked": FiWind,
      "30-Day Champion Badge Unlocked": FiCalendar,
      "Consistency Master Badge Unlocked": FiTarget,
      "Asana Adept Badge Unlocked": FiUser,
      "Wellness Guardian Badge Unlocked": FiShield,
      "Breathwork Sage Badge Unlocked": FiWind,
      "Century Practitioner Badge Unlocked": FiAward,
      "Nutrition Ninja Badge Unlocked": FiDroplet,
      "Level 3 Yogi Badge Unlocked": FiStar,
      "60-Day Streak Badge Unlocked": FiZap,
      "YTT Graduate Badge Unlocked": FiAward,
      "Global Yogi Badge Unlocked": FiTrendingUp,
      "Iron Practice Badge Unlocked": FiTarget
    };
    return achievementsData.milestones.map(m => ({
      ...m,
      icon: iconMap[m.title] || FiStar
    }));
  }, [achievementsData]);

  const CERTS = useMemo(() => {
    const apiCerts = achievementsData?.certs || achievementsData?.Certs || achievementsData?.certificates;
    return Array.isArray(apiCerts) ? apiCerts : [];
  }, [achievementsData]);

  const LEADERBOARD = useMemo(() => {
    if (!achievementsData?.leaderboard) return [];
    return achievementsData.leaderboard;
  }, [achievementsData]);

  const unlocked = achievementsData?.unlockedCount ?? 0;
  const totalPts = achievementsData?.totalPoints ?? 0;
  const userLevel = achievementsData?.level ?? "Level 1 Yogi";
  const userRankStr = achievementsData?.leaderboardRank ?? "—";

  const filtered = BADGES.filter(b => {
    const fc = activeFilter === "All" || b.cat === activeFilter.toLowerCase();
    
    let ft = true;
    if (activeTab === "completed") {
      ft = b.unlocked;
    } else if (activeTab === "active") {
      ft = !b.unlocked && b.progress > 0;
    } else if (activeTab === "locked") {
      ft = !b.unlocked && b.progress === 0;
    }
    
    return fc && ft;
  });

  // Hero ring values
  const R1 = 38, C1 = 2 * Math.PI * R1, O1 = C1 - (Math.min(totalPts, 3500) / 3500) * C1;

  if (loading) {
    return (
      <UserDashboardLayout>
        <Topbar />
        <BrandLoader 
          message="Loading Achievements..." 
          subMessage="Fetching your badges, medals, and streaks" 
        />
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div style={{ fontFamily: "'Inter',sans-serif" }}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

        {/* Ambient orbs */}
        <motion.div animate={{ y:[0,-22,0], x:[0,14,0] }} transition={{ duration:13, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[460px] h-[460px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.08),transparent 70%)", filter:"blur(80px)", top:-150, right:-90 }} />
        <motion.div animate={{ y:[0,16,0], x:[0,-10,0] }} transition={{ duration:16, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[380px] h-[380px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)", filter:"blur(80px)", bottom:-80, left:-80 }} />

        <div className="relative z-10 w-full min-w-0">
          <Topbar/>
          {/* ── HERO ── */}
          <motion.div initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}
            className="relative overflow-hidden rounded-[36px] p-6 sm:p-8 md:p-10 mb-6" style={glass}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03] rounded-[inherit]"
              style={{ backgroundImage:"radial-gradient(circle,#000 1px,transparent 0)", backgroundSize:"22px 22px" }} />
            <div className="absolute top-0 right-0 pointer-events-none"
              style={{ width:280, height:280, background:"radial-gradient(circle,rgba(242,154,12,0.09),transparent)", filter:"blur(40px)" }} />

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_340px] gap-6 lg:gap-8 items-center relative">
              <div>
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <motion.div animate={{ boxShadow:["0 0 0 0 rgba(242,154,12,0)","0 0 0 10px rgba(242,154,12,0.15)","0 0 0 0 rgba(242,154,12,0)"] }}
                    transition={{ duration:3, repeat:Infinity }}
                    className="w-11 h-11 rounded-[16px] flex items-center justify-center"
                    style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow:`0 8px 22px rgba(242,154,12,0.42)` }}>
                    <FiAward size={20} style={{ color:"#fff" }} />
                  </motion.div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[1.5px]" style={{ color:T.orange }}>Achievements</p>
                    <p className="text-[12px]" style={{ color:T.light }}>Your wellness legacy</p>
                  </div>
                  <div className="ml-auto sm:ml-3 flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-bold flex-shrink-0"
                    style={{ background:"rgba(107,138,90,0.1)", border:"1px solid rgba(107,138,90,0.2)", color:"#27500A" }}>
                    <FiStar size={11} /> {userLevel}
                  </div>
                </div>
                <h1 className="font-black mb-3 text-[26px] sm:text-[32px] lg:text-[36px]"
                  style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-1.5px", lineHeight:1.06, color:T.dark }}>
                  Every badge tells{" "}
                  <span style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                    your story.
                  </span>
                </h1>
                <p className="text-[14px] leading-[1.8] mb-7" style={{ color:T.muted, maxWidth:480 }}>
                  Unlock badges by achieving milestones, earning points, and completing your wellness journey. Each achievement is a step toward mastery.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <motion.button whileHover={{ y:-2, scale: 1.02 }} whileTap={{ scale:0.97 }}
                    onClick={async () => {
                      const userObj = localStorage.getItem("divyayoga_user") ? JSON.parse(localStorage.getItem("divyayoga_user")) : {};
                      const shareName = userObj.fullName || achievementsData?.userName || "Yogi";
                      const shareUrl = `${window.location.origin}/user/achievement`;
                      const shareData = {
                        title: `${shareName}'s Divyyoga Achievements`,
                        text: `Check out my yoga achievements, certificates, and ${totalPts} total points on Divyyog!🧘‍♀️✨`,
                        url: shareUrl
                      };

                      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
                        try {
                          await navigator.share(shareData);
                        } catch (err) {
                          if (err.name !== "AbortError") {
                            await navigator.clipboard.writeText(shareUrl);
                            toast.success("✓ Achievement Profile share link copied to clipboard! 🔗");
                          }
                        }
                      } else {
                        await navigator.clipboard.writeText(shareUrl);
                        toast.success("✓ Achievement Profile share link copied to clipboard! 🔗");
                      }
                    }}
                    className="h-11 px-6 rounded-full text-white text-[13px] font-extrabold flex items-center gap-2 shadow-lg cursor-pointer"
                    style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})`, border: "none", boxShadow: "0 8px 24px rgba(242,154,12,0.4)" }}>
                    <FiShare2 size={14} /> Share Profile
                  </motion.button>
                  <motion.button whileHover={{ y:-1 }} whileTap={{ scale:0.97 }}
                    onClick={() => document.getElementById("certificates-section")?.scrollIntoView({ behavior: "smooth" })}
                    className="h-11 px-5 rounded-full text-[13px] font-semibold flex items-center gap-2"
                    style={{ background:"rgba(255,255,255,0.9)", border:`1px solid ${T.border}`, color:T.muted, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    <FiDownload size={13} /> Certificates
                  </motion.button>
                </div>
              </div>

              {/* Dark points card */}
              <TiltCard intensity={5}
                className="rounded-[24px] p-6 relative overflow-hidden"
                style={{ background:"linear-gradient(145deg,#1F1F1F,#2D2D2D)", boxShadow:"0 24px 60px rgba(0,0,0,0.35)" }}>
                <div className="absolute inset-0 opacity-[0.07]"
                  style={{ backgroundImage:"radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize:"16px 16px" }} />
                <div className="absolute top-[-30px] right-[-30px] w-36 h-36 rounded-full opacity-15"
                  style={{ background:"radial-gradient(circle,rgba(242,154,12,0.5),transparent)" }} />
                <div className="relative">
                  <p className="text-[10px] font-bold uppercase tracking-[1.5px] mb-4" style={{ color:"rgba(255,255,255,0.45)" }}>
                    Total Points Earned
                  </p>
                  <div className="flex items-center gap-4 mb-5">
                    <div style={{ position:"relative", width:90, height:90, flexShrink:0 }}>
                      <svg viewBox="0 0 90 90" style={{ position:"absolute", inset:0, width:"100%", height:"100%", transform:"rotate(-90deg)" }}>
                        <circle cx="45" cy="45" r={R1} stroke="rgba(255,255,255,0.1)" strokeWidth="7" fill="none" />
                        <motion.circle cx="45" cy="45" r={R1} stroke="url(#ptg)" strokeWidth="7" fill="none"
                           strokeLinecap="round" strokeDasharray={C1}
                          initial={{ strokeDashoffset: C1 }} animate={{ strokeDashoffset: O1 }}
                          transition={{ duration:1.6, delay:0.3, ease:"easeOut" }} />
                        <defs>
                          <linearGradient id="ptg" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor={T.orange} /><stop offset="100%" stopColor={T.orange2} />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                        <p className="font-black text-white text-[14px] leading-none">{Math.round(Math.min(totalPts/3500, 1)*100)}%</p>
                        <p className="text-[8px]" style={{ color:"rgba(255,255,255,0.5)", marginTop:1 }}>to next</p>
                      </div>
                    </div>
                    <div>
                      <p className="font-black text-white leading-none"
                        style={{ fontFamily:"'Poppins',sans-serif", fontSize:38, letterSpacing:"-1.5px" }}>{totalPts.toLocaleString()}</p>
                      <p className="text-[11px] mt-1" style={{ color:"rgba(255,255,255,0.5)" }}>points this month</p>
                    </div>
                  </div>
                  {/* Level progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-[11px] mb-2">
                      <span style={{ color:"rgba(255,255,255,0.6)" }}>{totalPts >= 2500 ? "Master Yogi Achieved" : totalPts >= 1200 ? "Level 3 → Level 4" : totalPts >= 500 ? "Level 2 → Level 3" : "Level 1 → Level 2"}</span>
                      <span className="font-bold" style={{ color:T.orange }}>{totalPts.toLocaleString()} / 3500</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background:"rgba(255,255,255,0.1)" }}>
                      <motion.div className="h-full rounded-full"
                        style={{ background:`linear-gradient(90deg,${T.orange},${T.orange2})` }}
                        initial={{ width:0 }} animate={{ width:`${Math.round(Math.min(totalPts/3500,1)*100)}%` }}
                        transition={{ duration:1.2, delay:0.5, ease:"easeOut" }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    {[["Badges", unlocked], ["Certificates", CERTS.filter(c => c.unlocked).length]].map(([label, val]) => (
                      <div key={label} className="rounded-[12px] p-3" style={{ background:"rgba(255,255,255,0.07)" }}>
                        <p className="font-black text-white text-[18px] leading-none"
                          style={{ fontFamily:"'Poppins',sans-serif" }}>{val}</p>
                        <p className="text-[9px] font-bold uppercase tracking-wide mt-1"
                          style={{ color:"rgba(255,255,255,0.45)" }}>{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </TiltCard>
            </div>
          </motion.div>

          {/* ── STATS BAND ── */}
          <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.1 }}
            className="ud-kpi grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-0 mb-6 rounded-[24px] overflow-hidden"
            style={{ border:"1px solid rgba(255,255,255,0.9)", boxShadow:"0 12px 40px rgba(0,0,0,0.05)" }}>
            {[
              { icon:FiAward,      val:String(unlocked), label:"Badges Unlocked",  accent:T.orange, bg:"linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
              { icon:FiStar,       val:totalPts.toLocaleString(), label:"Total Points",   accent:T.olive,  bg:"linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
              { icon:FiDownload,   val:String(CERTS.filter(c => c.unlocked).length), label:"Certificates",    accent:T.orange, bg:"linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
              { icon:FiTrendingUp, val:userRankStr,             label:"Leaderboard Rank",accent:T.olive,  bg:"linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
            ].map((s, i) => (
              <motion.div key={s.label} whileHover={{ background:"rgba(255,255,255,0.95)" }}
                className="relative overflow-hidden px-6 py-5 flex items-center gap-3"
                style={{ background:s.bg, borderRight:i<3?`1px solid rgba(239,229,216,0.5)`:"none" }}>
                <div className="absolute bottom-0 left-0 right-0 h-[2px]"
                  style={{ background:`linear-gradient(90deg,${s.accent},${s.accent}88)` }} />
                <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0"
                  style={{ background:`linear-gradient(135deg,${s.accent},${s.accent}CC)`, boxShadow:`0 6px 16px ${s.accent}40` }}>
                  <s.icon size={16} style={{ color:"#fff" }} />
                </div>
                <div>
                  <p className="ud-kpi-val font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:24, letterSpacing:"-1px", lineHeight:1 }}>{s.val}</p>
                  <p className="ud-kpi-lbl text-[10px] font-bold uppercase tracking-[0.7px] mt-0.5" style={{ color:T.light }}>{s.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* ── FILTER + TABS ── */}
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.14 }}
            className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="ud-scroll flex gap-2 flex-wrap">
              {CATS.map(cat => (
                <motion.button key={cat} onClick={() => setActiveFilter(cat)} whileTap={{ scale:0.94 }}
                  className="px-4 py-2 rounded-full text-[12px] font-bold border-0 outline-none cursor-pointer"
                  style={activeFilter === cat
                    ? { background:`linear-gradient(135deg,${T.orange},${T.orange2})`, color:"#fff", boxShadow:`0 6px 18px rgba(242,154,12,0.38)`, fontFamily:"'Inter',sans-serif", transform:"translateY(-1px)" }
                    : { background:"rgba(255,255,255,0.82)", color:T.muted, border:`1px solid ${T.border}`, fontFamily:"'Inter',sans-serif" }
                  }>
                  {cat}
                </motion.button>
              ))}
            </div>
            <div className="ud-scroll flex gap-2">
              {["all", "completed", "active", "locked"].map(tab => (
                <motion.button key={tab} onClick={() => setActiveTab(tab)} whileTap={{ scale:0.94 }}
                  className="px-4 py-2 rounded-full text-[12px] font-bold border-0 outline-none cursor-pointer capitalize"
                  style={activeTab === tab
                    ? { background:`linear-gradient(135deg,${T.orange},${T.orange2})`, color:"#fff", boxShadow:`0 6px 18px rgba(242,154,12,0.38)`, fontFamily:"'Inter',sans-serif" }
                    : { background:"rgba(255,255,255,0.82)", color:T.muted, border:`1px solid ${T.border}`, fontFamily:"'Inter',sans-serif" }
                  }>
                  {tab === "all" ? "All" : tab === "completed" ? "Completed Badge" : tab === "active" ? "Active Badge" : "Locked"}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* ── BADGE GRID ── */}
          <AnimatePresence mode="wait">
            <motion.div key={activeFilter + activeTab}
              initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
              className="grid gap-4 mb-8"
              style={{ gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))" }}>
              {filtered.map((b, i) => (
                <BadgeCard key={b.id} badge={b} delay={i * 0.04} onClick={setBadgeModal} />
              ))}
            </motion.div>
          </AnimatePresence>

          {/* ── MILESTONE TIMELINE ── */}
          <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
            className="mb-8">
            <h2 className="font-black text-[22px] text-[#1F1F1F] mb-1"
              style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>Milestone Timeline</h2>
            <p className="text-[13px] mb-5" style={{ color:T.muted }}>Your biggest moments on the mat</p>

            <div className="rounded-[28px] p-7" style={glass}>
              <div className="relative pl-11">
                {/* Vertical line */}
                <div className="absolute left-[14px] top-0 bottom-0 w-[2px] rounded-full"
                  style={{ background:`linear-gradient(180deg,${T.orange},${T.orange2},${T.olive},rgba(107,138,90,0.2))` }} />
                {MILESTONES.map((m, i) => {
                  const Icon = m.icon;
                  return (
                    <motion.div key={m.date}
                      initial={{ opacity:0, x:-16 }} whileInView={{ opacity:1, x:0 }}
                      viewport={{ once:true }} transition={{ delay:i*0.08 }}
                      className="flex gap-4 mb-5 last:mb-0">
                      <motion.div
                        animate={m.active ? { boxShadow:["0 0 0 0 rgba(242,154,12,0)","0 0 0 8px rgba(242,154,12,0.15)","0 0 0 0 rgba(242,154,12,0)"] } : {}}
                        transition={{ duration:2.5, repeat:Infinity }}
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background:`linear-gradient(135deg,${m.accent},${m.accent}CC)`, boxShadow:`0 4px 12px ${m.accent}45` }}>
                        <Icon size={14} style={{ color:"#fff" }} />
                      </motion.div>
                      <motion.div
                        whileHover={{ x:5 }}
                        className="flex items-center justify-between flex-1 rounded-[16px] px-4 py-3"
                        style={{ background:m.bg, border:"1px solid rgba(255,255,255,0.9)", transition:"transform 0.25s" }}>
                        <div>
                          <p className="font-black text-[13px] text-[#1F1F1F]"
                            style={{ fontFamily:"'Poppins',sans-serif" }}>{m.title}</p>
                          <p className="text-[11px] mt-0.5" style={{ color:T.light }}>{m.date}</p>
                        </div>
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold flex-shrink-0"
                          style={{ background:"rgba(107,138,90,0.1)", color:"#27500A" }}>
                          <FiAward size={10} /> {m.pts}
                        </div>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* ── CERTIFICATES + LEADERBOARD ── */}
          <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
            className="grid xl:grid-cols-[1fr_340px] gap-5">

            {/* Certificates */}
            <div id="certificates-section">
              <div className="flex justify-between items-center mb-1">
                <h2 className="font-black text-[22px] text-[#1F1F1F]"
                  style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>International Certificates</h2>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  {CERTS.filter(c => c.unlocked).length} of {CERTS.length} Earned
                </span>
              </div>
              <p className="text-[13px] mb-5" style={{ color:T.muted }}>Official International Board credentials unlocked by completing practice milestones</p>

              <div className="flex flex-col gap-4">
                {CERTS.length === 0 && (
                  <div className="rounded-[22px] p-8 text-center border border-dashed"
                    style={{ borderColor: "rgba(242,154,12,0.35)", background: "rgba(255,255,255,0.6)" }}>
                    <div className="w-12 h-12 rounded-[16px] mx-auto mb-3 flex items-center justify-center"
                      style={{ background: "rgba(242,154,12,0.12)" }}>
                      <FiAward size={22} style={{ color: T.orange }} />
                    </div>
                    <p className="text-[14px] font-bold text-[#1F1F1F]">No certificates yet</p>
                    <p className="text-[12px] mt-1" style={{ color: T.muted }}>
                      Keep practising. Certificates appear here as you complete program milestones.
                    </p>
                  </div>
                )}
                {CERTS.map((c, i) => (
                  <motion.div key={c.title}
                    initial={{ opacity:0, y:16 }} whileInView={{ opacity:1, y:0 }}
                    viewport={{ once:true }} transition={{ delay:i*0.07 }}
                    whileHover={{ y:-4, boxShadow: c.unlocked ? "0 22px 60px rgba(0,0,0,0.12)" : "0 10px 30px rgba(0,0,0,0.04)" }}
                    className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-[22px] p-5"
                    style={{ 
                      background: c.unlocked ? "rgba(255,255,255,0.92)" : "linear-gradient(145deg, rgba(250,248,245,0.95), rgba(240,236,230,0.95))",
                      border: c.unlocked ? `1px solid rgba(242,154,12,0.25)` : `1px dashed rgba(180,180,180,0.4)`,
                      boxShadow: c.unlocked ? "0 16px 50px rgba(0,0,0,0.06), inset 0 1px 0 #fff" : "none",
                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.25,1,0.5,1)" 
                    }}
                    onClick={() => {
                      setCertModal(c);
                    }}>
                    <div className="w-14 h-14 rounded-[18px] flex items-center justify-center flex-shrink-0"
                      style={{ 
                        background: c.unlocked ? `linear-gradient(135deg,${c.accent},${c.accent2})` : "linear-gradient(135deg, rgba(200,195,188,0.8), rgba(170,165,158,0.8))", 
                        boxShadow: c.unlocked ? `0 8px 20px ${c.accent}45` : "none" 
                      }}>
                      {c.unlocked ? (
                        <FiAward size={24} style={{ color:"#fff" }} />
                      ) : (
                        <FiLock size={22} style={{ color:"#fff" }} />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-black text-[14px] text-[#1F1F1F] mb-0"
                          style={{ fontFamily:"'Poppins',sans-serif" }}>{c.title}</p>
                        {c.unlocked ? (
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-300">UNLOCKED</span>
                        ) : (
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-700 border border-zinc-300">LOCKED</span>
                        )}
                      </div>

                      <p className="text-[11px] mb-1.5" style={{ color:T.muted }}>{c.prog} · {c.unlocked ? `Issued: ${c.date}` : `Req: ${c.requirement}`}</p>
                      
                      {!c.unlocked && (
                        <div className="w-full max-w-xs">
                          <div className="flex justify-between items-center text-[9.5px] font-bold mb-1">
                            <span className="text-zinc-500">Progress</span>
                            <span className="text-[#E97818]">{c.progress || 0}%</span>
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-zinc-200 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-600" style={{ width: `${c.progress || 0}%` }} />
                          </div>
                        </div>
                      )}
                    </div>

                    {c.unlocked ? (
                      <motion.button whileHover={{ y:-1, scale: 1.02 }} whileTap={{ scale:0.95 }}
                        onClick={e => { e.stopPropagation(); setCertModal(c); }}
                        className="h-9 px-4 rounded-full font-bold text-[11px] text-white flex items-center justify-center gap-1.5 flex-shrink-0"
                        style={{ background:`linear-gradient(135deg,${c.accent},${c.accent2})`,
                          boxShadow:`0 5px 14px ${c.accent}45`, border:"none", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                        <FiDownload size={11} /> Download PDF
                      </motion.button>
                    ) : (
                      <button onClick={e => { e.stopPropagation(); setCertModal(c); }}
                        className="h-9 px-4 rounded-full font-bold text-[11px] text-zinc-600 bg-zinc-200 border border-zinc-300 flex items-center justify-center gap-1.5 flex-shrink-0 hover:bg-zinc-300 transition-all cursor-pointer">
                        <FiLock size={11} /> View Requirements
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Leaderboard */}
            <div>
              <h2 className="font-black text-[22px] text-[#1F1F1F] mb-1"
                style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>Leaderboard</h2>
              <p className="text-[13px] mb-5" style={{ color:T.muted }}>Top performers this month</p>
              <div className="rounded-[28px] p-5" style={glass}>
                {LEADERBOARD.length === 0 && (
                  <div className="py-8 text-center">
                    <FiTrendingUp size={22} className="mx-auto mb-2" style={{ color: T.olive }} />
                    <p className="text-[14px] font-bold text-[#1F1F1F]">No rankings yet</p>
                    <p className="text-[12px] mt-1" style={{ color: T.muted }}>
                      Earn badge points to appear on the leaderboard.
                    </p>
                  </div>
                )}
                {LEADERBOARD.map((p, i) => (
                  <motion.div key={p.rank}
                    initial={{ opacity:0, x:16 }} whileInView={{ opacity:1, x:0 }}
                    viewport={{ once:true }} transition={{ delay:i*0.06 }}
                    whileHover={{ x:5 }}
                    className="flex items-center gap-3.5 rounded-[18px] px-4 py-3 mb-2 last:mb-0"
                    style={{ background:p.me ? "rgba(255,244,232,0.7)" : "transparent",
                      border:p.me ? "2px solid rgba(242,154,12,0.35)" : "2px solid transparent",
                      transition:"all 0.25s" }}>
                    <div className="w-7 text-center flex-shrink-0 flex justify-center">
                      {p.rank <= 3 ? (
                        <motion.span initial={{ scale:0 }} animate={{ scale:1 }}
                          transition={{ delay:i*0.06+0.3, type:"spring", stiffness:300 }}
                          className="flex justify-center items-center">
                          <FiStar size={18} style={{ 
                            color: p.rank === 1 ? "#D4AF37" : p.rank === 2 ? "#A0A0A0" : "#CD7F32",
                            fill: p.rank === 1 ? "#D4AF37" : p.rank === 2 ? "#A0A0A0" : "#CD7F32"
                          }} />
                        </motion.span>
                      ) : (
                        <span className="font-black text-[14px]" style={{ fontFamily:"'Poppins',sans-serif", color:T.light }}>
                          #{p.rank}
                        </span>
                      )}
                    </div>
                    <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0 font-black text-white text-[12px]"
                      style={{ background:`linear-gradient(135deg,${p.accent},${p.accent}CC)`,
                        boxShadow:`0 4px 12px ${p.accent}40`, fontFamily:"'Poppins',sans-serif" }}>
                      {p.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-[13px] text-[#1F1F1F]">{p.name}</p>
                        {p.me && (
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background:"rgba(242,154,12,0.1)", color:"#C8660A" }}>You</span>
                        )}
                      </div>
                      <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>{p.pts.toLocaleString()} pts</p>
                    </div>
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0"
                      style={{ background:p.rank <= 3 ? "rgba(242,154,12,0.1)" : "rgba(200,200,200,0.15)",
                        color:p.rank <= 3 ? "#C8660A" : "#888" }}>
                      {TIER_CONFIG[p.tier]?.label || "Bronze"}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── MODALS ── */}
      <AnimatePresence>
        {badgeModal && <BadgeModal badge={badgeModal} onClose={() => setBadgeModal(null)} />}
        {certModal  && <CertModal  cert={certModal}  onClose={() => setCertModal(null)}  />}
      </AnimatePresence>
    </UserDashboardLayout>
  );
}